import { z } from "zod/v4-mini";

import { ControlIQData } from "@/lib/drivers/tandem/commands/ControlIQ";
import { EventList } from "@/lib/drivers/tandem/commands/Event";
import { GlobalData } from "@/lib/drivers/tandem/commands/GlobalData";
import { MaxBolus } from "@/lib/drivers/tandem/commands/MaxBolus";
import { PersonalProfile } from "@/lib/drivers/tandem/commands/PersonalProfile";
import { TandemVersionInfo } from "@/lib/drivers/tandem/commands/Version";
import { Schema } from "@/utils/zodUtils";

export type TandemResponse<T, U = T> = {
  value: number;
  name: string;
  version?: number;
  format: string;
  fields: string[];
  postprocess: (payload: T) => U;
  schema: Schema<T>;
};

export type TandemCommand<T = undefined, U = T> = T extends undefined
  ? {
      value: number;
      name: string;
      version?: number;
      format?: string;
      fields?: string[];
    }
  : {
      value: number;
      name: string;
      version?: number;
      format?: string;
      fields?: string[];
      response: TandemResponse<T, U>;
    };

export type TandemEventDefinition<T, U = T> = {
  value: number;
  name: string;
  format: string;
  fields: string[];
  postprocess: (payload: T) => U;
  schema: Schema<T>;
};

export type TandemPacket<T> = {
  valid: boolean;
  sync: number;
  descriptor: number;
  payload_len: number;
  payload: T;
  crc: number;
  packet_len: number;
  body: unknown;
  timestamp?: number;
};

export type TandemLastUploadInfo = {
  last_fetched_sequence: number;
};

export const tandemUploadInfoSchema = z.object({
  last_fetched_sequence: z.number(),
});

export type TandemPumpSettings = {
  globals: GlobalData;
  maxBolus?: MaxBolus;
  profiles: PersonalProfile[];
  controlIq?: ControlIQData;
};

export type TandemPumpEvents = EventList & {
  startSeq: number;
  endSeq: number;
};

export type TandemData = {
  settings: TandemPumpSettings;
  events: TandemPumpEvents;
  versions: TandemVersionInfo;
};

export type TandemImportRawInfo = {
  import_start_date: string;
  import_end_date: string;
  version_info: TandemVersionInfo;
  data: TandemData;
  raw_packets: TandemPacket<unknown>[];
};
